import type { BrickType, OptimizedBrick, LayerData } from "./types";
import { LEGO_BRICK_TYPES } from "./brickTypes";

// Brick categories for multi-pass optimization
enum BrickCategory {
  LARGE = "large",      // 2x4, 2x6, 4x4, 2x8, 4x6, 4x8 - for main structure
  MEDIUM = "medium",    // 2x2, 2x3, 1x4, 1x3, 2x2p, 2x4p - for secondary areas
  SMALL = "small",      // 1x1, 1x2, 1x1p, 1x2p - for details
}

interface EdgeMap {
  edges: boolean[][];
  detected: boolean;
}

export class EnhancedBrickOptimizer {
  private pixelData: number[][];
  private width: number;
  private height: number;
  private brickTypes: BrickType[];
  private categorizedBricks: Map<BrickCategory, BrickType[]>;

  constructor(pixelData: number[][], width: number, height: number) {
    this.pixelData = pixelData;
    this.width = width;
    this.height = height;
    this.brickTypes = LEGO_BRICK_TYPES;
    this.categorizedBricks = this.categorizeBricks();
  }

  // Categorize bricks by size for multi-pass optimization
  private categorizeBricks(): Map<BrickCategory, BrickType[]> {
    const categories = new Map<BrickCategory, BrickType[]>();
    categories.set(BrickCategory.LARGE, []);
    categories.set(BrickCategory.MEDIUM, []);
    categories.set(BrickCategory.SMALL, []);

    for (const brick of this.brickTypes) {
      const area = brick.width * brick.height;

      // Large bricks: area >= 8 (2x4, 2x6, 4x4, etc.)
      if (area >= 8) {
        categories.get(BrickCategory.LARGE)!.push(brick);
      }
      // Medium bricks: area between 4 and 7 (2x2, 2x3, 1x4, etc.)
      else if (area >= 4) {
        categories.get(BrickCategory.MEDIUM)!.push(brick);
      }
      // Small bricks: area < 4 (1x1, 1x2, etc.)
      else {
        categories.get(BrickCategory.SMALL)!.push(brick);
      }
    }

    return categories;
  }

  // Main optimization function with multi-pass strategy
  optimize(maxDepth: number = 1): LayerData[] {
    const layers: LayerData[] = [];

    for (let layer = 0; layer < maxDepth; layer++) {
      const layerPixels = this.extractLayerPixels(layer);
      if (this.hasNonZeroPixels(layerPixels)) {
        const layerData = this.optimizeLayerMultiPass(layerPixels, layer);
        layers.push(layerData);
      }
    }

    return layers;
  }

  // Extract pixels for a specific layer
  private extractLayerPixels(layer: number): number[][] {
    return this.pixelData.map((row) => [...row]);
  }

  // Check if there are non-zero pixels
  private hasNonZeroPixels(pixels: number[][]): boolean {
    return pixels.some((row) => row.some((pixel) => pixel !== 0));
  }

  // Multi-pass optimization: large bricks first, then medium, then small
  private optimizeLayerMultiPass(pixels: number[][], layerIndex: number): LayerData {
    const workingPixels = pixels.map((row) => [...row]);
    const bricks: OptimizedBrick[] = [];
    const colorUsage: { [key: string]: number } = {};

    // Detect edges for detail-aware placement
    const edgeMap = this.detectEdges(workingPixels);

    // Pass 1: Large bricks for main structure (avoid edges)
    this.optimizationPass(
      workingPixels,
      layerIndex,
      BrickCategory.LARGE,
      bricks,
      colorUsage,
      edgeMap,
      true // avoidEdges
    );

    // Pass 2: Medium bricks for secondary areas (avoid edges)
    this.optimizationPass(
      workingPixels,
      layerIndex,
      BrickCategory.MEDIUM,
      bricks,
      colorUsage,
      edgeMap,
      true // avoidEdges
    );

    // Pass 3: Small bricks for details and remaining pixels (fill everywhere)
    this.optimizationPass(
      workingPixels,
      layerIndex,
      BrickCategory.SMALL,
      bricks,
      colorUsage,
      edgeMap,
      false // fillRemaining - don't avoid edges
    );

    return {
      layer: layerIndex,
      bricks,
      brickCount: bricks.length,
      colorUsage,
    };
  }

  // Single optimization pass with specific brick category
  private optimizationPass(
    pixels: number[][],
    layerIndex: number,
    category: BrickCategory,
    bricks: OptimizedBrick[],
    colorUsage: { [key: string]: number },
    edgeMap: EdgeMap,
    avoidEdges: boolean
  ): void {
    const categoryBricks = this.categorizedBricks.get(category) || [];

    // Sort by area (largest first), then by commonness
    const sortedBricks = [...categoryBricks].sort((a, b) => {
      const areaA = a.width * a.height;
      const areaB = b.width * b.height;

      if (areaA !== areaB) return areaB - areaA;
      if (a.isCommon !== b.isCommon) return b.isCommon ? 1 : -1;
      return areaB - areaA;
    });

    // Iterate through pixels
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (pixels[y][x] === 0) continue;

        const colorIndex = pixels[y][x];

        // If avoiding edges, skip edge pixels for large/medium bricks
        if (avoidEdges && edgeMap.detected && this.isNearEdge(x, y, edgeMap)) {
          continue;
        }

        // Try to place the largest possible brick
        let bestBrick: OptimizedBrick | null = null;

        for (const brickType of sortedBricks) {
          if (this.canPlaceBrick(pixels, x, y, brickType, colorIndex)) {
            bestBrick = {
              x,
              y,
              z: layerIndex,
              brickType,
              colorIndex,
              colorName: "",
              rotation: 0,
            };
            break;
          }
        }

        if (bestBrick) {
          this.markPixelsUsed(pixels, bestBrick);
          bricks.push(bestBrick);

          const colorKey = `color_${colorIndex}`;
          colorUsage[colorKey] = (colorUsage[colorKey] || 0) + 1;
        }
      }
    }
  }

  // Detect edges in the pixel data for detail-aware placement
  private detectEdges(pixels: number[][]): EdgeMap {
    const edges: boolean[][] = [];
    let hasEdges = false;

    for (let y = 0; y < this.height; y++) {
      edges[y] = [];
      for (let x = 0; x < this.width; x++) {
        if (pixels[y][x] === 0) {
          edges[y][x] = false;
          continue;
        }

        const currentColor = pixels[y][x];
        let isEdge = false;

        // Check neighboring pixels for color differences
        const neighbors = [
          [-1, 0], [1, 0], [0, -1], [0, 1], // 4-direction
          [-1, -1], [-1, 1], [1, -1], [1, 1], // 8-direction
        ];

        for (const [dy, dx] of neighbors) {
          const ny = y + dy;
          const nx = x + dx;

          if (ny >= 0 && ny < this.height && nx >= 0 && nx < this.width) {
            if (pixels[ny][nx] !== 0 && pixels[ny][nx] !== currentColor) {
              isEdge = true;
              hasEdges = true;
              break;
            }
          }
        }

        edges[y][x] = isEdge;
      }
    }

    return { edges, detected: hasEdges };
  }

  // Check if a position is near an edge
  private isNearEdge(x: number, y: number, edgeMap: EdgeMap): boolean {
    const edgeRadius = 1; // Check 1 pixel around

    for (let dy = -edgeRadius; dy <= edgeRadius; dy++) {
      for (let dx = -edgeRadius; dx <= edgeRadius; dx++) {
        const ny = y + dy;
        const nx = x + dx;

        if (ny >= 0 && ny < this.height && nx >= 0 && nx < this.width) {
          if (edgeMap.edges[ny] && edgeMap.edges[ny][nx]) {
            return true;
          }
        }
      }
    }

    return false;
  }

  // Check if a brick can be placed at the given position
  private canPlaceBrick(
    pixels: number[][],
    x: number,
    y: number,
    brickType: BrickType,
    colorIndex: number,
  ): boolean {
    if (
      x + brickType.width > this.width ||
      y + brickType.height > this.height
    ) {
      return false;
    }

    for (let dy = 0; dy < brickType.height; dy++) {
      for (let dx = 0; dx < brickType.width; dx++) {
        const targetY = y + dy;
        const targetX = x + dx;

        if (targetY >= this.height || targetX >= this.width) {
          return false;
        }

        if (pixels[targetY][targetX] !== colorIndex) {
          return false;
        }
      }
    }

    return true;
  }

  // Mark pixels as used by a brick
  private markPixelsUsed(pixels: number[][], brick: OptimizedBrick): void {
    const { x, y, brickType } = brick;

    for (let dy = 0; dy < brickType.height; dy++) {
      for (let dx = 0; dx < brickType.width; dx++) {
        pixels[y + dy][x + dx] = 0;
      }
    }
  }

  // Calculate optimization statistics
  getOptimizationStats(
    originalPixelCount: number,
    optimizedBricks: OptimizedBrick[],
  ): {
    pieceReduction: number;
    efficiency: number;
    brickTypeUsage: { [key: string]: number };
    largeBrickRatio: number;
  } {
    const brickTypeUsage: { [key: string]: number } = {};
    let totalBrickArea = 0;
    let largeBrickCount = 0;

    optimizedBricks.forEach((brick) => {
      const typeKey = brick.brickType.id;
      brickTypeUsage[typeKey] = (brickTypeUsage[typeKey] || 0) + 1;
      totalBrickArea += brick.brickType.width * brick.brickType.height;

      const area = brick.brickType.width * brick.brickType.height;
      if (area >= 8) largeBrickCount++;
    });

    const pieceReduction =
      ((originalPixelCount - optimizedBricks.length) / originalPixelCount) *
      100;
    const efficiency = (totalBrickArea / originalPixelCount) * 100;
    const largeBrickRatio = (largeBrickCount / optimizedBricks.length) * 100;

    return {
      pieceReduction,
      efficiency,
      brickTypeUsage,
      largeBrickRatio,
    };
  }
}
